import {
  Stack,
  Avatar,
  useMediaQuery, Divider
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Users } from "lucide-react";
import {
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { SharedItemCard } from "./SharedItemCard.component";

import type { TopicCardProps } from "./TopicCardShare.types";

import {
  CtaCommunityOverlay,
  ModifyContentModal,
  CommentsSection
} from "..";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { DeleteContentModal } from "../../components/DeleteContentModal";
import {
  ActionLink,
  BodyTypography,
  OwnerNameTypography,
  TopicCardTop
} from "../../components/TopicCard/TopicCard.styles";
import { TopicCardSignature } from "../../components/TopicCardSignature";
import { POST_TYPE, ROLES } from "../../consts";
import { useAuthStore } from "../../stores";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";
import { stringAvatar, useGetCreatedLabel } from "../../utils/community";
import { BadgeBox } from "../Commons/BadgeBox";

export function TopicCardShare(props: TopicCardProps) {
  const {
    body,
    changed,
    commentsNumber,
    created,
    id,
    learningObjectTypology,
    likes,
    ownerImage,
    ownerName,
    sharedLo,
    selectedTopic,
    selectedFilter,
    topicOwner
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isModifyOpen, setIsModifyOpen] = useState(false);
  const[showComments,setShowComments]=useState(false);

  const userData = useAuthStore(state => state.userData);

  const isModerator = userData?.roles.some(role => role === ROLES.BUSINESS_ADMIN || role === ROLES.PRODUCT_OWNER) ;
  const userId = useAuthStore(state => state.userData?.id_user);

  const isEditable =  (userId && topicOwner == userId.toString());
  const isModified = created !== changed;

  return (
    <CommonWidgetContainer
      sx={ {
        width:"100% !important",
        maxWidth:"100%" ,
        "& p": {
          color: theme.customColors.exploreCardText
        },
        "& span": {
          color: theme.customColors.exploreCardText
        },
        "& .icon path": {
          fill: theme.customColors.exploreCardText + "!important"
        }
      } }
      hasSidePadding={ true }
      hoverBorderAccent={ false }
    >
      <Stack
        sx={ {
          position: "relative"
        } }
        gap={ "24px" }
      >
        <TopicCardTop
        >
          <Avatar
            alt={ `${t("avatar_user")} ${ownerName}` }
            { ...stringAvatar(ownerName) }
            src={ ownerImage }
            sx={ {
              height: isMobile ? "32px" : "56px",
              width: isMobile ? "32px" : "56px"
            } }
          />
          <Stack
            flexGrow={ 1 }
            gap={ 1 }
          >
            <Stack
              direction={ "row" }
              justifyContent={ "space-between" }
              alignItems={ "center" }
            >
              <OwnerNameTypography>{ ownerName }</OwnerNameTypography>
              <BadgeBox
                background={ theme.customColors.backgroundSecondary }
                textColor={ theme.customColors.textTag }
                border={ `1px solid ${theme.customColors.textTag}` }>
                <Stack
                  direction={ "row" }
                  alignItems={ "center" }
                  gap={ "8px" }
                >
                  <Users size={ 16 }/>
                  <TypographyTextTag>
                    { t("share") }
                  </TypographyTextTag>
                </Stack>
              </BadgeBox>
            </Stack>
            <Stack
              direction={ "row" }
              justifyContent={ "space-between" }
              alignItems={ "center" }
              flexWrap={ "wrap" }>
              <TopicCardSignature
                created={ useGetCreatedLabel( created) }
                isModified={ isModified }
                size={ "medium" }
              />
              {
                isEditable  || isModerator ?
                  <Stack
                    direction="row"
                    alignItems={ "flex-end" }
                    gap={ 2 }
                  >
                    { isEditable ?

                      <ActionLink
                        onClick={ () => setIsModifyOpen(true) }
                      >
                        { t("modify") }
                      </ActionLink>  :
                      null
                    }

                    <ActionLink
                      onClick={ () => setIsDeleteOpen(true) }
                      sx={ { color:theme.customColors.accentVariantA } }
                    >
                      { t("delete") }
                    </ActionLink>
                  </Stack>  :
                  null
              }
            </Stack>
          </Stack>
        </TopicCardTop>

        <BodyTypography >
          { body }
        </BodyTypography>

        {
          sharedLo && learningObjectTypology ?
            <>
              <Divider/>
              <SharedItemCard
                title={ sharedLo.title }
                shortDescription={ sharedLo.description }
                id={ Number(sharedLo.lo_id) }
                learningObjectTypology={ learningObjectTypology }
                coverPublicURL={ sharedLo.publicCoverUrl }
                topicTags={ sharedLo.topics }
              />
              <Divider/>
            </>:
            null
        }


        <CtaCommunityOverlay
          id={ id }
          comments={ commentsNumber }
          likes={ likes }
          commentsOpened={ showComments }
          commentsClickAction={ () => {
            setShowComments(!showComments);
          } }
          selectedFilter={ selectedFilter }
          selectedTopic={ selectedTopic }
        />

        {
          isDeleteOpen && sharedLo ?

            <DeleteContentModal
              closeDrawer={ ()=> {
                setIsDeleteOpen(false);
              } }
              id={ id }
              isModerator={ Boolean(isModerator) }
              loId={ sharedLo.lo_id }
              onClose={ ()=> {
                setIsDeleteOpen(false);
              } }
              open={ isDeleteOpen }
              postType={ POST_TYPE.SHARED }
              selectedTopic={ selectedTopic }
              selectedFilter={ selectedFilter }
            /> : null
        }

        {
          isModifyOpen && sharedLo?
            <ModifyContentModal
              selectedFilter={ selectedFilter }
              selectedTopic={ selectedTopic }
              open={ isModifyOpen }
              onClose={ () => setIsModifyOpen(false) }
              id={ id }
              loId={ sharedLo.lo_id }
              postType={ POST_TYPE.SHARED }
              body={ body }
              closeDrawer={ () => setIsModifyOpen(false) }
            />
            : null

        }

      </Stack>

      <CommentsSection
        topicOwnerId={ topicOwner }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
        discussionId={ id }
        enabled={ showComments }
        className={ showComments ? "visible" : "" } />

    </CommonWidgetContainer>
  );
}
